/**
 * EMA Livestock Delivery Date Picker
 * =====================================
 * Multi-instance custom calendar widget. Supports both the main cart page
 * and the cart drawer slide-out simultaneously without ID conflicts.
 *
 * Each .ema-dd-root element gets its own isolated instance via makeWidgetInstance().
 * The cart drawer is monitored via MutationObserver on the aria-hidden attribute
 * of the <cart-drawer> custom element so the widget updates reliably on open.
 *
 * Dispatch schedule (for reference — staff only):
 *   Livestock Courier (overnight): dispatch Mon–Wed → delivers Wed–Fri
 *   Airfreight (same-day):         dispatch Wed–Fri → delivers Wed–Fri
 *
 * Valid delivery days for customers: Wednesday – Friday only.
 */

(function () {
  'use strict';

  // ── Constants ────────────────────────────────────────────────────────────────
  var LIVESTOCK_TYPES       = ['fish', 'coral', 'invert', 'livestock', 'anemone'];
  var MIN_DAYS_AHEAD        = 1;
  var MAX_DAYS_AHEAD        = 8;
  var EXCLUDED_DAYS         = [0, 1, 2, 6]; // Sun, Mon, Tue, Sat
  var COURIER_DISPATCH_DAYS = [2, 3, 4]; // Tue, Wed, Thu

  // ── Specific blocked delivery dates (YYYY-MM-DD) ─────────────────────────
  var BLOCKED_DATES = [
    '2026-05-19', // Closed — no deliveries
  ];
  var MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
  var DAY_NAMES   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  // Shared save timeout (cart attributes are shared across all instances)
  var saveTimeout = null;

  // ── Date helpers ─────────────────────────────────────────────────────────────

  function pad(n) { return String(n).padStart(2, '0'); }

  function toInputValue(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function toHumanDate(d) {
    return DAY_NAMES[d.getDay()] + ', ' + d.getDate() + ' ' + MONTH_NAMES[d.getMonth()] + ' ' + d.getFullYear();
  }

  function toShortDate(d) {
    return d.getDate() + ' ' + MONTH_NAMES[d.getMonth()] + ' ' + d.getFullYear();
  }

  function addDays(base, n) {
    var d = new Date(base);
    d.setDate(d.getDate() + n);
    return d;
  }

  function today() {
    var parts = new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    var value = {};
    parts.forEach(function (part) { value[part.type] = part.value; });
    return new Date(Number(value.year), Number(value.month) - 1, Number(value.day));
  }

  function minDate() {
    var d = addDays(today(), MIN_DAYS_AHEAD);
    while (EXCLUDED_DAYS.indexOf(d.getDay()) !== -1) d = addDays(d, 1);
    return d;
  }

  function maxDate() {
    return addDays(today(), MAX_DAYS_AHEAD);
  }

  function isValidDeliveryDay(d) {
    if (EXCLUDED_DAYS.indexOf(d.getDay()) !== -1) return false;
    var iso = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    return BLOCKED_DATES.indexOf(iso) === -1;
  }

  function courierDispatchDate(deliveryDate) {
    var d = addDays(deliveryDate, -1);
    return COURIER_DISPATCH_DAYS.indexOf(d.getDay()) !== -1 ? d : null;
  }

  function parseHumanDate(str) {
    if (!str) return null;
    var cleaned = str.replace(/^[^,]+,\s*/, '').trim();
    var parts = cleaned.split(' ');
    if (parts.length < 3) return null;
    var monthMap = {};
    MONTH_NAMES.forEach(function (m, i) { monthMap[m.toLowerCase()] = i; });
    var day   = parseInt(parts[0], 10);
    var month = monthMap[parts[1].toLowerCase()];
    var year  = parseInt(parts[2], 10);
    if (isNaN(day) || month === undefined || isNaN(year)) return null;
    var d = new Date(year, month, day);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // ── Livestock detection ──────────────────────────────────────────────────────

  function isLivestockType(pt) {
    if (!pt) return false;
    var lower = pt.toLowerCase();
    for (var i = 0; i < LIVESTOCK_TYPES.length; i++) {
      if (lower.indexOf(LIVESTOCK_TYPES[i]) !== -1) return true;
    }
    return false;
  }

  function detectLivestock() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        for (var i = 0; i < items.length; i++) {
          if (isLivestockType(items[i].product_type)) return true;
          var tags = items[i].product ? (items[i].product.tags || '') : '';
          if (typeof tags === 'string') {
            tags = tags.split(',').map(function (t) { return t.trim().toLowerCase(); });
          }
          for (var j = 0; j < tags.length; j++) {
            var t = tags[j];
            if (t === 'livestock' || t === 'live' || t === 'live fish' || t === 'live coral') return true;
          }
        }
        return false;
      })
      .catch(function () { return false; });
  }

  // ── Cart attribute save ──────────────────────────────────────────────────────

  function normaliseDeliveryMethod(method) {
    var lower = String(method || '').trim().toLowerCase();
    if (lower === 'pickup') return 'Pickup';
    if (lower === 'shipping') return 'Shipping';
    return '';
  }

  function saveCartAttributes(method, deliveryDate) {
    method = normaliseDeliveryMethod(method);
    var attrs = {
      'EMA Delivery Method': method,
      'EMA Delivery Date ISO': '',
      'Desired Delivery Date': '',
      'Dispatch Date (Airfreight)': '',
      'Dispatch Date (Overnight Courier)': '',
    };

    if (method === 'Shipping' && deliveryDate) {
      var cd = courierDispatchDate(deliveryDate);
      attrs['EMA Delivery Date ISO'] = toInputValue(deliveryDate);
      attrs['Desired Delivery Date'] = toHumanDate(deliveryDate);
      attrs['Dispatch Date (Airfreight)'] = toShortDate(deliveryDate);
      attrs['Dispatch Date (Overnight Courier)'] = cd ? toShortDate(cd) : 'N/A – airfreight only for this date';
    }

    window.__emaDeliveryStateSave = fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ attributes: attrs }),
    }).catch(function (err) {
      console.error('[EMA] delivery state save failed:', err);
    });

    return window.__emaDeliveryStateSave;
  }

  // ── Hidden form-input sync (belt-and-braces for non-AJAX cart submit) ────────

  function syncFormState(method, deliveryDate) {
    var values = {
      'EMA Delivery Method': normaliseDeliveryMethod(method),
      'EMA Delivery Date ISO': deliveryDate ? toInputValue(deliveryDate) : '',
      'Desired Delivery Date': deliveryDate ? toHumanDate(deliveryDate) : '',
    };

    ['cart', 'cart-drawer-form'].forEach(function (formId) {
      var form = document.getElementById(formId);
      if (!form) return;

      Object.keys(values).forEach(function (key) {
        var selector = 'input[name="attributes[' + key + ']"]';
        var existing = form.querySelector(selector);
        if (!existing) {
          existing = document.createElement('input');
          existing.type = 'hidden';
          existing.name = 'attributes[' + key + ']';
          form.appendChild(existing);
        }
        existing.value = values[key] || '';
      });
    });
  }

  // ── Checkout button lock/unlock ──────────────────────────────────────────────
  // Targets both the cart-page form button AND the cart-drawer checkout button.

  function applyLock(btn, locked) {
    if (!btn) return;
    if (locked) {
      btn.disabled = true;
      btn.setAttribute('title', 'Please select a delivery date for your livestock before checking out.');
      btn.style.opacity = '0.5';
      btn.style.cursor  = 'not-allowed';
    } else {
      btn.disabled = false;
      btn.removeAttribute('title');
      btn.style.opacity = '';
      btn.style.cursor  = '';
    }
  }

  function setCheckoutLocked(locked) {
    // Cart page: button is inside <form id="cart">
    var cartForm = document.getElementById('cart');
    if (cartForm) applyLock(cartForm.querySelector('[name="checkout"]'), locked);

    // Cart drawer: button uses form="cart-drawer-form" and lives outside the form tag
    applyLock(document.querySelector('[form="cart-drawer-form"][name="checkout"]'), locked);
  }

  // ── Per-instance widget factory ──────────────────────────────────────────────

  function makeWidgetInstance(rootEl) {
    var calEl         = rootEl.querySelector('.ema-dd-calendar');
    var errorEl       = rootEl.querySelector('.ema-dd-error');
    var confirmEl     = rootEl.querySelector('.ema-dd-confirm');
    var confirmText   = rootEl.querySelector('.ema-dd-confirm-text');
    var choiceModeEl  = rootEl.querySelector('.ema-dd-choice-mode');
    var calendarModeEl = rootEl.querySelector('.ema-dd-calendar-mode');
    var choiceButtons = rootEl.querySelectorAll('.ema-dd-choice-btn');
    var backBtn       = rootEl.querySelector('.ema-dd-back-btn');

    // Per-instance state
    var calViewYear       = null;
    var calViewMonth      = null;
    var calSelected       = null;
    var lastHasLivestock  = null;
    var currentMode       = 'choice'; // 'choice' or 'calendar'
    var selectedChoice    = null;     // 'pickup' or 'shipping'

    // ── Mode switching ─────────────────────────────────────────────────────────

    function setMode(mode) {
      currentMode = mode;
      if (mode === 'choice') {
        if (choiceModeEl) choiceModeEl.removeAttribute('hidden');
        if (calendarModeEl) calendarModeEl.setAttribute('hidden', '');
      } else if (mode === 'calendar') {
        if (choiceModeEl) choiceModeEl.setAttribute('hidden', '');
        if (calendarModeEl) calendarModeEl.removeAttribute('hidden');
      }
    }

    function setChoiceButtonState(choice) {
      choiceButtons.forEach(function (btn) {
        if (btn.getAttribute('data-choice') === choice) {
          btn.classList.add('ema-dd-choice-btn--selected');
        } else {
          btn.classList.remove('ema-dd-choice-btn--selected');
        }
      });
    }

    function selectChoice(choice) {
      selectedChoice = choice;
      setChoiceButtonState(choice);

      if (choice === 'pickup') {
        // Pickup is a complete fulfilment choice: no delivery date is required.
        calSelected = null;
        if (confirmEl) confirmEl.hidden = true;
        setMode('choice');
        rootEl.setAttribute('data-method', 'Pickup');
        rootEl.setAttribute('data-saved', '');
        syncFormState('Pickup', null);
        saveCartAttributes('Pickup', null);
        setCheckoutLocked(false);
      } else if (choice === 'shipping') {
        // Shipping must have a fresh valid date. Clear any stale date first.
        setMode('calendar');
        if (calViewYear === null) {
          var startDate = minDate();
          calViewYear  = startDate.getFullYear();
          calViewMonth = startDate.getMonth();
        }
        calSelected = null;
        if (confirmEl) confirmEl.hidden = true;
        rootEl.setAttribute('data-method', 'Shipping');
        rootEl.setAttribute('data-saved', '');
        syncFormState('Shipping', null);
        saveCartAttributes('Shipping', null);
        renderCalendar();
        setCheckoutLocked(true);
      }
    }

    // ── Calendar renderer ──────────────────────────────────────────────────────────

    function renderCalendar() {
      if (!calEl) return;

      var mn     = minDate();
      var mx     = maxDate();
      var todayD = today();

      if (calViewYear === null) {
        calViewYear  = mn.getFullYear();
        calViewMonth = mn.getMonth();
      }

      var prevLastDay  = new Date(calViewYear, calViewMonth, 0);
      var nextFirstDay = new Date(calViewYear, calViewMonth + 1, 1);
      var canPrev = prevLastDay >= mn;
      var canNext = nextFirstDay <= mx;

      var firstDay    = new Date(calViewYear, calViewMonth, 1);
      var lastDay     = new Date(calViewYear, calViewMonth + 1, 0);
      var startOffset = (firstDay.getDay() + 6) % 7; // Monday-start

      var html = [];

      // Nav row
      html.push(
        '<div class="ema-cal__nav">' +
          '<button type="button" class="ema-cal__nav-btn ema-cal__nav-prev"' +
            (canPrev ? '' : ' disabled') + ' aria-label="Previous month">&#8249;</button>' +
          '<span class="ema-cal__month-label">' +
            MONTH_NAMES[calViewMonth] + ' ' + calViewYear +
          '</span>' +
          '<button type="button" class="ema-cal__nav-btn ema-cal__nav-next"' +
            (canNext ? '' : ' disabled') + ' aria-label="Next month">&#8250;</button>' +
        '</div>'
      );

      // Day-of-week headers (Wed–Fri active, Mon/Tue/Sat/Sun dimmed)
      var dowLabels = ['Mo','Tu','We','Th','Fr','Sa','Su'];
      var dowDims   = [true,true,false,false,false,true,true];
      html.push('<div class="ema-cal__dow">');
      for (var h = 0; h < 7; h++) {
        html.push(
          '<span class="ema-cal__dow-label' + (dowDims[h] ? ' ema-cal__dow-label--dim' : '') + '">' +
            dowLabels[h] +
          '</span>'
        );
      }
      html.push('</div>');

      // Day grid
      html.push('<div class="ema-cal__grid" role="grid" aria-label="' + MONTH_NAMES[calViewMonth] + ' ' + calViewYear + '">');

      for (var e = 0; e < startOffset; e++) {
        html.push('<span class="ema-cal__day ema-cal__day--empty" aria-hidden="true"></span>');
      }

      for (var d = 1; d <= lastDay.getDate(); d++) {
        var date = new Date(calViewYear, calViewMonth, d);
        date.setHours(0, 0, 0, 0);

        var excluded   = EXCLUDED_DAYS.indexOf(date.getDay()) !== -1;
        var tooEarly   = date < mn;
        var tooLate    = date > mx;
        var available  = !excluded && !tooEarly && !tooLate;
        var isToday    = date.getTime() === todayD.getTime();
        var isSelected = calSelected && date.getTime() === calSelected.getTime();

        var cls = ['ema-cal__day'];
        if (available)  cls.push('ema-cal__day--available');
        else            cls.push('ema-cal__day--unavailable');
        if (isSelected) cls.push('ema-cal__day--selected');
        if (isToday && available) cls.push('ema-cal__day--today');

        var ds    = toInputValue(date);
        var label = toHumanDate(date) + (available ? '' : ' — not available');

        if (available) {
          html.push(
            '<button type="button" class="' + cls.join(' ') + '"' +
              ' data-date="' + ds + '"' +
              ' aria-label="' + label + '"' +
              (isSelected ? ' aria-pressed="true"' : '') +
            '>' + d + '</button>'
          );
        } else {
          html.push(
            '<span class="' + cls.join(' ') + '" aria-label="' + label + '" aria-disabled="true">' + d + '</span>'
          );
        }
      }

      var total = startOffset + lastDay.getDate();
      var trail = total % 7 === 0 ? 0 : 7 - (total % 7);
      for (var t = 0; t < trail; t++) {
        html.push('<span class="ema-cal__day ema-cal__day--empty" aria-hidden="true"></span>');
      }

      html.push('</div>');
      calEl.innerHTML = html.join('');

      // Bind month nav
      var prevBtn = calEl.querySelector('.ema-cal__nav-prev');
      var nextBtn = calEl.querySelector('.ema-cal__nav-next');
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          calViewMonth--;
          if (calViewMonth < 0) { calViewMonth = 11; calViewYear--; }
          renderCalendar();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          calViewMonth++;
          if (calViewMonth > 11) { calViewMonth = 0; calViewYear++; }
          renderCalendar();
        });
      }

      // Bind day buttons
      var dayBtns = calEl.querySelectorAll('.ema-cal__day[data-date]');
      for (var i = 0; i < dayBtns.length; i++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            var parts = btn.getAttribute('data-date').split('-');
            var sel = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
            sel.setHours(0, 0, 0, 0);
            calSelected = sel;
            renderCalendar();
            onDateSelected(sel, true);
          });
        })(dayBtns[i]);
      }
    }

    // ── Date selected ──────────────────────────────────────────────────────────

    function onDateSelected(date, triggerSave) {
      if (confirmEl && confirmText) {
        confirmText.textContent = 'Delivery date: ' + toHumanDate(date);
        confirmEl.hidden = false;
      }
      if (errorEl) errorEl.hidden = true;
      setCheckoutLocked(false);
      rootEl.setAttribute('data-method', 'Shipping');
      rootEl.setAttribute('data-saved', toHumanDate(date));
      syncFormState('Shipping', date);
      if (triggerSave !== false) {
        clearTimeout(saveTimeout);
        saveTimeout = setTimeout(function () { saveCartAttributes('Shipping', date); }, 300);
      }
    }

    // ── Show / hide widget ─────────────────────────────────────────────────────

    function setWidgetVisible(visible) {
      rootEl.hidden = !visible;
      if (!visible) {
        setCheckoutLocked(false);
        return;
      }

      var mn = minDate();
      var mx = maxDate();

      var savedMethod = normaliseDeliveryMethod(rootEl.getAttribute('data-method') || '');
      var saved = rootEl.getAttribute('data-saved') || '';

      // Pickup is self-contained and must never require a delivery date.
      if (savedMethod === 'Pickup') {
        selectedChoice = 'pickup';
        setChoiceButtonState('pickup');
        setMode('choice');
        calSelected = null;
        if (confirmEl) confirmEl.hidden = true;
        syncFormState('Pickup', null);
        setCheckoutLocked(false);
        return;
      }

      // Restore a valid shipping date. If this is a legacy cart with a date but
      // no saved method, infer Shipping for backwards compatibility.
      if (saved) {
        var parsed = parseHumanDate(saved);
        if (parsed && parsed >= mn && parsed <= mx && isValidDeliveryDay(parsed)) {
          calSelected  = parsed;
          calViewYear  = parsed.getFullYear();
          calViewMonth = parsed.getMonth();
          selectedChoice = 'shipping';
          setChoiceButtonState('shipping');
          setMode('calendar');
          renderCalendar();
          onDateSelected(parsed, false);
          setCheckoutLocked(false);
          return;
        }
      }

      // Shipping was chosen but no valid date remains: require a new date.
      if (savedMethod === 'Shipping') {
        selectedChoice = 'shipping';
        setChoiceButtonState('shipping');
        setMode('calendar');
        if (calViewYear === null) {
          calViewYear = mn.getFullYear();
          calViewMonth = mn.getMonth();
        }
        renderCalendar();
        syncFormState('Shipping', null);
        setCheckoutLocked(true);
        return;
      }

      // No fulfilment choice yet.
      setMode('choice');
      selectedChoice = null;
      setChoiceButtonState(null);
      syncFormState('', null);
      setCheckoutLocked(true);
    }

    // ── Event listeners ────────────────────────────────────────────────────────

    function bindEventListeners() {
      // Choice buttons
      choiceButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var choice = btn.getAttribute('data-choice');
          selectChoice(choice);
        });
      });

      // Back button
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          setMode('choice');
        });
      }
    }

    // ── Refresh (re-detect livestock and update visibility) ────────────────────

    function refresh() {
      detectLivestock().then(function (hasLivestock) {
        if (hasLivestock !== lastHasLivestock) {
          // Livestock state changed — update fully
          lastHasLivestock = hasLivestock;
          setWidgetVisible(hasLivestock);
          if (!hasLivestock) {
            calSelected = null;
            selectedChoice = null;
            if (errorEl)   errorEl.hidden = true;
            if (confirmEl) confirmEl.hidden = true;
            syncFormState('', null);
            saveCartAttributes('', null);
          }
        } else if (hasLivestock && rootEl.hidden) {
          // State unchanged but widget is hidden (e.g. drawer was AJAX re-rendered —
          // the new DOM node starts hidden so we must explicitly show it).
          setWidgetVisible(true);
        }
      });
    }

    // ── Initial load ───────────────────────────────────────────────────────────

    bindEventListeners();
    detectLivestock().then(function (hasLivestock) {
      lastHasLivestock = hasLivestock;
      setWidgetVisible(hasLivestock);
    });

    // Expose rootEl so the coordinator can check whether this node is still in DOM
    return { refresh: refresh, rootEl: rootEl };
  }

  // ── Init coordinator ─────────────────────────────────────────────────────────

  function init() {
    // instances is mutable — entries are pruned when their DOM node is removed
    var instances = [];

    // Attach a widget instance to every uninitialised .ema-dd-root on the page.
    // We mark each root with data-ema-init so we never double-init the same node.
    function ensureInstances() {
      var roots = document.querySelectorAll('.ema-dd-root:not([data-ema-init])');
      for (var i = 0; i < roots.length; i++) {
        roots[i].setAttribute('data-ema-init', '1');
        instances.push(makeWidgetInstance(roots[i]));
      }
    }

    function refreshAll() {
      // 1. Pick up any new .ema-dd-root nodes injected by AJAX re-renders
      ensureInstances();

      // 2. Refresh live instances; prune any whose root was removed from the DOM
      var live = [];
      for (var j = 0; j < instances.length; j++) {
        if (document.contains(instances[j].rootEl)) {
          instances[j].refresh();
          live.push(instances[j]);
        }
        // Stale instance (detached node) is simply dropped — if the theme re-injects
        // a fresh .ema-dd-root, ensureInstances() above will have already attached a
        // new instance to it.
      }
      instances = live;
    }

    ensureInstances(); // attach to roots present at page load

    // Poll every 2s (catches cases where no events fire at all)
    setInterval(refreshAll, 2000);

    // Theme AJAX cart events — cover all known variants across Shopify themes
    ['cart:updated', 'cart:change', 'cart:item-added', 'cart-drawer:open',
     'cart-drawer:refreshed', 'theme:cart:updated'].forEach(function (evName) {
      document.addEventListener(evName, refreshAll);
    });

    // Intercept window.fetch calls to Shopify cart-write endpoints.
    // When add-to-cart completes, trigger refreshAll so the widget appears
    // in the drawer without waiting for the next poll tick.
    if (typeof window.fetch === 'function' && !window._emaFetchPatched) {
      window._emaFetchPatched = true;
      var _origFetch = window.fetch;
      window.fetch = function (resource, init) {
        var url = (typeof resource === 'string'
          ? resource
          : (resource && typeof resource.url === 'string' ? resource.url : '')) || '';
        var isCartWrite = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
        var promise = _origFetch.apply(this, arguments);
        if (isCartWrite) {
          promise.then(function () {
            // First refresh shortly after the request completes (cart data now updated)
            setTimeout(refreshAll, 300);
            // Second pass in case the drawer re-renders after a short delay
            setTimeout(refreshAll, 1200);
          }).catch(function () {});
        }
        return promise;
      };
    }

    // Watch the <cart-drawer> element for two things:
    //   a) aria-hidden flip → drawer opened → refresh
    //   b) childList subtree mutations → AJAX re-render injected new HTML → pick up new root
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      var reRenderDebounce = null;

      var obs = new MutationObserver(function (mutations) {
        var drawerOpened = false;
        var contentChanged = false;

        for (var m = 0; m < mutations.length; m++) {
          if (mutations[m].type === 'attributes' && mutations[m].attributeName === 'aria-hidden') {
            var hidden = drawerEl.getAttribute('aria-hidden');
            if (hidden === 'false' || hidden === null) drawerOpened = true;
          }
          if (mutations[m].type === 'childList') {
            contentChanged = true;
          }
        }

        if (drawerOpened) {
          refreshAll();
        } else if (contentChanged) {
          // Debounce — the theme may fire many childList mutations during one re-render
          clearTimeout(reRenderDebounce);
          reRenderDebounce = setTimeout(function () {
            ensureInstances();
            // Also force a refresh so the newly-attached instance shows immediately
            // if the drawer happens to be open right now
            if (drawerEl.getAttribute('aria-hidden') !== 'true') refreshAll();
          }, 120);
        }
      });

      obs.observe(drawerEl, {
        attributes:    true,
        attributeFilter: ['aria-hidden'],
        childList:     true,
        subtree:       true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
